/**
 * Shared types + helpers for the pool admin detail page.
 *
 * The detail route ($poolId.tsx) fetches all data and owns all mutating
 * handlers; the tab components under this directory are presentational
 * consumers of these types. Everything here used to live inline in the
 * 4.3k-line route file — see .docs/pool-admin-redesign.md.
 */

import { SUPER_REGION_OPTIONS } from '../../constants/regions';
import styles from '../../routes/admin/pools/PoolDetail.module.css';

// ---------------------------------------------------------------------------
// URL + formatting helpers
// ---------------------------------------------------------------------------

export function getApiBaseUrl(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  return wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

export function poolUrl(poolId: string, path: string, pool?: AdminPool | null): string {
  const orgId = pool && pool.type !== 'by_request' ? pool.orgId : 'global';
  return `${getApiBaseUrl()}/pool/${orgId}:${poolId}${path}`;
}

export function regionLabel(code: string): string {
  return SUPER_REGION_OPTIONS.find(r => r.value === code)?.label ?? code;
}

export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

export function relativeMs(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

export function formatDuration(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

export const REFRESH_INTERVAL_MS = 10_000;

// ---------------------------------------------------------------------------
// Data types (shapes served by the gateway admin + PoolDO debug endpoints)
// ---------------------------------------------------------------------------

export interface AdminPool {
  id: string;
  name: string;
  type: string;
  region: string;
  settingsJson: string | null;
  capacityScheduleJson: string | null;
  active: boolean;
  createdAt: string;
  projectId: string;
  projectName: string;
  orgId: string;
  orgName: string;
}

export interface PoolCounts {
  total: number;
  available: number;
  busy: number;
  reconciling: number;
  draining: number;
  pinned: number;
  totalSessionSlots: number;
  usedSessionSlots: number;
  availableSessionSlots: number;
}

export interface PoolOverride {
  autoscalingPaused: boolean;
  pausedAt?: number;
  pauseReason?: string;
  scaling?: {
    desiredCapacity: number;
    minSize: number;
    maxSize: number;
    createdAt: number;
  };
}

export interface ScalingDecisionRecord {
  id: number;
  at: number;
  strategyName: string;
  action: 'scale-up' | 'scale-down' | 'hold';
  currentSessionCapacity: number;
  targetSessionCapacity: number;
  reason: string;
  confidence: number;
  totalWorkers: number;
  availableWorkers: number;
  busyWorkers: number;
  totalSessionSlots: number;
  usedSessionSlots: number;
  demandUnmet: number | null;
  demandAvgWaitMs: number | null;
  activeScheduleWindow: string | null;
  executed: boolean;
}

export interface ScaleActionRecord {
  at: number;
  direction: 'up' | 'down';
  fromInstanceCount: number;
  toInstanceCount: number;
  reason: string;
}

export interface DemandSnapshot {
  fetchedAt: number;
  totalUnmet: number;
  avgWaitMs: number;
  matchRate: number;
  cells: Array<{ cellId: string; unmet: number }>;
  /** Populated when the row was recorded from a push-based unmet-demand signal. */
  appId?: string;
  versionId?: string;
}

export interface ScaleUpLatencyRecord {
  id: number;
  requestedAt: number;
  fromWorkers: number;
  toWorkers: number;
  timeToFirstWorkerMs: number | null;
  timeToTargetMs: number | null;
  status: string;
  reason: string;
}

export interface EnrichedInstance {
  instanceId: string;
  backendId: string;
  status: string;
  orchestratorId: string | null;
  publicIp?: string;
}

export interface BoostEntry {
  id: number;
  addedSlots: number;
  createdAt: number;
  expiresAt: number;
  reason: string;
  createdBy: string;
}

export interface DampenEntry {
  id: number;
  removedSlots: number;
  createdAt: number;
  expiresAt: number;
  reason: string;
  createdBy: string;
}

export interface PoolStats {
  ok: boolean;
  poolId: string;
  counts: PoolCounts;
  override: PoolOverride;
  boosts?: BoostEntry[];
  dampens?: DampenEntry[];
  maxConcurrentByBackend: Record<string, number>;
  scalingDecisions: ScalingDecisionRecord[];
  scaleActions: ScaleActionRecord[];
  demandHistory: DemandSnapshot[];
  scaleUpLatency: ScaleUpLatencyRecord[];
}

export interface OrchestratorInfo {
  workerId: string;
  instanceId: string;
  cloud: string;
  region: string;
  zone?: string;
  state: 'registered' | 'available' | 'busy' | 'reconciling' | 'draining';
  provisionedApps: Array<{ appId: string; versionId: string; ready: boolean }>;
  activeJobs: Array<{ jobId: string; appId: string; versionId?: string; sessionId: string; startedAt: number }>;
  registeredAt: string;
  lastPingAt: string;
  /** Drain-reaper idle clock: when the worker last went fully idle. Absent while busy. */
  idleSince?: string;
  pinned: boolean;
  managed: boolean;
  agentVersion?: string;
  os?: string;
  arch?: string;
  activeSessions: number;
  maxConcurrentSessions: number;
}

export interface ProvisioningBackend {
  backendId: string;
  cloud: string;
  status: string;
  error?: string;
}

export interface ProvisioningState {
  status: string;
  backends?: ProvisioningBackend[];
}

export interface InfraScaling {
  currentCapacity: number;
  desiredCapacity: number;
  /** Desired capacity expressed in session slots (machines × maxConcurrent). */
  desiredSessionCapacity?: number;
  minSize: number;
  maxSize: number;
  isStable: boolean;
  instances?: Array<{ instanceId: string; lifecycleState: string }>;
}

export interface InfraHealthBackend {
  backendId: string;
  cloud: string;
  region: string;
  healthy: boolean;
  currentCapacity: number;
  desiredCapacity: number;
  lastError?: string;
  /**
   * Classification of `lastError`, surfaced verbatim from the backend health
   * snapshot. "transient" = self-resolving (e.g. a GCP MIG still settling in
   * the 30–60s window after create); "structural" = needs a human (bad config,
   * drift); "none" = no error since the last success. The UI uses this to avoid
   * painting a normal settle window as a hard error.
   */
  lastErrorKind?: 'transient' | 'structural' | 'none';
  /**
   * Consecutive failed health checks (resets to 0 on any success). Lets the UI
   * tell a *benign* transient (the tail of a settle, failures === 0) apart from
   * a backend that is *degrading* — transient errors piling up toward the
   * unhealthy threshold — so the latter doesn't hide under the calm "settling".
   */
  consecutiveFailures?: number;
  lastCheckedAt: number;
}

export interface InfraHealth {
  healthy: boolean;
  backends: InfraHealthBackend[];
}

export interface InfraState {
  scaling: InfraScaling | null;
  health: InfraHealth | null;
  instances: EnrichedInstance[];
}

// Predictive observability rows (IL-137).
export interface PredictionRow {
  timestampMs: number;
  forecastValue: number;
  contributed: number;
  actualDemand: number;
  coldStartStage: number;
  nEff: number;
  mode: string;
  region: string | null;
  reason: string | null;
}

export interface BinRow {
  tBin: number;
  nEff: number;
  weeksObserved: number;
  lastUpdatedAt: number;
}

export interface QuarantineRow {
  id: number;
  tBin: number;
  observation: number;
  reason: string;
  atMs: number;
}

// ---------------------------------------------------------------------------
// Badge helpers
// ---------------------------------------------------------------------------

export function orchestratorStateBadgeClass(state: string): string {
  switch (state) {
    case 'available': return styles.stateAvailable;
    case 'busy': return styles.stateBusy;
    case 'reconciling': return styles.stateProvisioning;
    case 'draining': return styles.stateDraining;
    case 'registered': return styles.stateRegistered;
    default: return styles.badge;
  }
}

export function provisioningBadgeClass(status: string): string {
  if (status === 'provisioned') return styles.provProvisioned;
  if (status === 'failed') return styles.provFailed;
  return styles.provPending;
}

export type BackendDisplayKind = 'healthy' | 'settling' | 'warning' | 'error' | 'idle';

export interface BackendDisplay {
  kind: BackendDisplayKind;
  label: string;
  badgeClass: string;
  /** Class for the per-backend `lastError` detail line, matched to severity. */
  noteClass: string;
  /** Hover explanation — surfaces *why* the badge is what it is. */
  title: string;
}

/**
 * Derive one honest display state for a backend from the health snapshot the
 * gateway already sends. A backend that is healthy but still bringing capacity
 * up — the normal GCP regional-MIG settle window (~30–60s after create), or any
 * current < desired ramp — must read as "settling", not as a red error.
 *
 * Red (`error`) is reserved for backends that are genuinely down
 * (`healthy === false` — structural error, exhausted transient, GCP stockout
 * give-up, or CoreWeave stuck-pending) or whose provisioning lifecycle
 * hard-failed. The amber tiers split by *intent*: "settling" is the calm,
 * expected ramp; "warning" is healthy-but-degrading or self-recovering and
 * wants a glance. Settling and warning are distinguished by badge *weight*
 * (see CSS), never by color or motion alone, so the distinction survives
 * screenshots, reduced-motion, and the themes where amber collapses into the
 * text color. Precedence is severity-ordered; the first matching rule wins.
 */
export function backendDisplayState(
  b: InfraHealthBackend,
  provStatus?: string,
): BackendDisplay {
  const deficit = Math.max(0, b.desiredCapacity - b.currentCapacity);
  const failures = b.consecutiveFailures ?? 0;

  // 1. Genuinely down — the only state that earns red.
  if (provStatus === 'failed' || !b.healthy) {
    return {
      kind: 'error',
      label: provStatus === 'failed' ? 'FAIL' : 'ERR',
      badgeClass: styles.provFailed,
      noteClass: styles.backendError,
      title: b.lastError ?? 'Backend unhealthy — needs attention',
    };
  }

  // 2. Healthy and ramping up: instances are launching. The live count is the
  //    progress signal — calm amber, not a red alarm.
  if (deficit > 0) {
    return {
      kind: 'settling',
      label: `STARTING ${deficit}`,
      badgeClass: styles.provSettling,
      noteClass: styles.backendNote,
      title: 'Instances launching — normal; a fresh GCP MIG settles in ~30–60s',
    };
  }

  // 3. Healthy and at capacity, but a structural condition is flagged that the
  //    backend is self-recovering from. Today the only structural-while-healthy
  //    producer is GCP's describe() drift path ("MIG gone; recovery armed").
  //    Wants a glance, but it is not an outage.
  if (b.lastError && b.lastErrorKind === 'structural') {
    return {
      kind: 'warning',
      label: 'CHECK',
      badgeClass: styles.provWarning,
      noteClass: styles.backendNote,
      title: b.lastError,
    };
  }

  // 4. Healthy and at capacity, but transient errors are *accumulating*
  //    (failures > 0) — degrading toward unhealthy, not calmly settling. Show
  //    the heavier warning rather than the reassuring ramp, so a backend that
  //    is one strike from going red doesn't wear the "everything's fine" badge.
  if (b.lastError && failures > 0) {
    return {
      kind: 'warning',
      label: 'CHECK',
      badgeClass: styles.provWarning,
      noteClass: styles.backendNote,
      title: `Retrying after errors (${failures}): ${b.lastError}`,
    };
  }

  // 5. Healthy and at capacity, with a benign transient note still clearing
  //    (no accumulated failures — the tail of a settle, e.g. a resize rejected
  //    inside the MIG window). Self-resolves on the next tick.
  if (b.lastError && b.lastErrorKind === 'transient') {
    return {
      kind: 'settling',
      label: 'SETTLING',
      badgeClass: styles.provSettling,
      noteClass: styles.backendNote,
      title: b.lastError,
    };
  }

  // 6. Healthy and at capacity with an *unclassified* error present — don't hide
  //    a possibly-real problem under the calm settle tier; surface it to look at.
  if (b.lastError) {
    return {
      kind: 'warning',
      label: 'CHECK',
      badgeClass: styles.provWarning,
      noteClass: styles.backendNote,
      title: b.lastError,
    };
  }

  // 7. Healthy but nothing requested (warm target 0): provisioned and parked.
  if (b.desiredCapacity === 0 && b.currentCapacity === 0) {
    return {
      kind: 'idle',
      label: 'IDLE',
      badgeClass: styles.provPending,
      noteClass: styles.backendNote,
      title: 'No capacity requested (warm target 0)',
    };
  }

  // 8. Healthy and serving its desired capacity.
  return {
    kind: 'healthy',
    label: 'OK',
    badgeClass: styles.provProvisioned,
    noteClass: styles.backendNote,
    title: 'Healthy',
  };
}
